<template>
  <v-card outlined class="mb-3">
    <v-card-title class="text-h6">
      Enviar notificación por carnet
    </v-card-title>

    <v-card-text>
      <v-container fluid class="pa-0">
        <v-row>
          <!-- Ciclo fijo (viene del padre) -->
          <v-col cols="12" sm="4">
            <v-text-field
              :value="ciclo"
              label="Ciclo"
              dense
              outlined
              hide-details
              readonly
            />
          </v-col>

          <v-col cols="12" sm="5">
            <v-text-field
              v-model="carnet"
              v-mask="'####-AA-###'"
              label="Carnet"
              dense
              outlined
              hide-details
              :disabled="loading"
              color="primary"
              @keyup.enter="enviarNotificacion"
            />
          </v-col>

          <v-col cols="12" sm="3" class="d-flex align-center">
            <v-btn
              color="primary"
              block
              :loading="loading"
              :disabled="loading"
              @click="enviarNotificacion"
            >
              ENVIAR
            </v-btn>
          </v-col>

          <v-col cols="12" v-if="errorMsg">
            <v-alert type="error" dense text class="mb-0">
              {{ errorMsg }}
            </v-alert>
          </v-col>

          <v-col cols="12" v-if="successMsg">
            <v-alert type="success" dense text class="mb-0">
              {{ successMsg }}
            </v-alert>
          </v-col>

          <!-- Si hay más de una coincidencia -->
          <v-col cols="12" v-if="candidatos.length > 1">
            <v-select
              v-model="solicitudSeleccionadaId"
              :items="candidatos"
              item-text="label"
              item-value="solicitudBecaId"
              label="Se encontraron varias solicitudes. Selecciona una:"
              outlined
              dense
              :disabled="loading"
            />
          </v-col>

          <v-col cols="12" v-if="candidatos.length > 1">
            <v-btn
              color="primary"
              block
              :loading="loading"
              :disabled="loading || !solicitudSeleccionadaId"
              @click="reenviarSeleccionada"
            >
              ENVIAR A LA SELECCIONADA
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import axios from "axios"
import { mask } from "vue-the-mask"

export default {
  name: "NotificacionPorCarnet",
  directives: { mask },

  props: {
    // ciclo viene del padre (selectCiclo)
    ciclo: { type: String, required: true }
  },

  data: () => ({
    carnet: "",
    loading: false,
    candidatos: [],
    solicitudSeleccionadaId: null,
    errorMsg: "",
    successMsg: ""
  }),

  methods: {
    async enviarNotificacion() {
      this.errorMsg = ""
      this.successMsg = ""
      this.candidatos = []
      this.solicitudSeleccionadaId = null

      const carnet = (this.carnet || "").trim()
      const ciclo = (this.ciclo || "").trim()

      if (!ciclo) {
        this.errorMsg = "No hay ciclo seleccionado."
        return
      }
      if (!carnet) {
        this.errorMsg = "Ingresa el carnet."
        return
      }

      this.loading = true
      let soliId = null
      let correo = ""

      try {
        // 1) Intento directo por carnet + ciclo
        let solicitudes = []
        try {
          const r1 = await axios.get(
            `api/SolicitudDeBeca?Carnet=${encodeURIComponent(
              carnet
            )}&CicloDeAplicacion=${encodeURIComponent(ciclo)}`
          )
          solicitudes = Array.isArray(r1.data) ? r1.data : []
        } catch {
          // 2) Fallback: listar por ciclo y filtrar localmente
          const r2 = await axios.get(
            `api/SolicitudDeBeca?CicloDeAplicacion=${encodeURIComponent(ciclo)}`
          )
          const all = Array.isArray(r2.data) ? r2.data : []
          solicitudes = all.filter(
            (x) => (x.carnet || "").toUpperCase() === carnet.toUpperCase()
          )
        }

        if (!solicitudes.length) {
          this.errorMsg = `No se encontró solicitud para ${carnet} en el ciclo ${ciclo}.`
          return
        }

        // Guardamos email para mostrarlo en el mensaje (igual que SolicitudesBecas)
        this.candidatos = solicitudes.map((s) => ({
          solicitudBecaId: s.solicitudBecaId,
          email: s.email || "",
          label: `Caso #${s.solicitudBecaId} - Ref ${s.numeroReferencia || "N/D"} - ${s.email || "Sin correo"}`
        }))

        if (this.candidatos.length > 1) {
          this.errorMsg =
            "Hay más de una solicitud con ese carnet en el ciclo. Selecciona una."
          return
        }

        soliId = this.candidatos[0].solicitudBecaId
        correo = this.candidatos[0].email

        await axios.post(
          `api/SolicitudDeBeca/ReenviarNotificacion/${soliId}`
        )

        this.successMsg =
          `Notificación reenviada correctamente.\n` +
          `Carnet: ${carnet}\n` +
          `Ciclo: ${ciclo}\n` +
          `Correo: ${correo || "correo no disponible"}\n` +
          `Caso: #${soliId}`

        this.$emit("notificacion-enviada", {
          solicitudBecaId: soliId,
          carnet,
          ciclo
        })
      } catch (e) {
        const status = e?.response?.status

        // El backend envía el correo aunque responda 400
        if (status === 400 && soliId) {
          this.successMsg =
            `Notificación reenviada correctamente.\n` +
            `Carnet: ${carnet}\n` +
            `Ciclo: ${ciclo}\n` +
            `Correo: ${correo || "correo no disponible"}\n` +
            `Caso: #${soliId}`

          this.errorMsg = ""
          this.$emit("notificacion-enviada", {
            solicitudBecaId: soliId,
            carnet,
            ciclo
          })
          return
        }

        this.errorMsg =
          "No fue posible enviar la notificación. Intenta nuevamente."
      } finally {
        this.loading = false
      }
    },

    async reenviarSeleccionada() {
      if (!this.solicitudSeleccionadaId) return

      this.loading = true
      this.errorMsg = ""
      this.successMsg = ""

      const carnet = (this.carnet || "").trim()
      const ciclo = (this.ciclo || "").trim()

      const found = this.candidatos.find(
        (c) => c.solicitudBecaId === this.solicitudSeleccionadaId
      )
      const correo = found?.email || ""

      try {
        await axios.post(
          `api/SolicitudDeBeca/ReenviarNotificacion/${this.solicitudSeleccionadaId}`
        )

        this.successMsg =
          `Notificación reenviada correctamente.\n` +
          `Carnet: ${carnet}\n` +
          `Ciclo: ${ciclo}\n` +
          `Correo: ${correo || "correo no disponible"}\n` +
          `Caso: #${this.solicitudSeleccionadaId}`

        this.$emit("notificacion-enviada", {
          solicitudBecaId: this.solicitudSeleccionadaId,
          carnet,
          ciclo
        })
      } catch (e) {
        const status = e?.response?.status

        if (status === 400 && this.solicitudSeleccionadaId) {
          this.successMsg =
            `Notificación reenviada correctamente.\n` +
            `Carnet: ${carnet}\n` +
            `Ciclo: ${ciclo}\n` +
            `Correo: ${correo || "correo no disponible"}\n` +
            `Caso: #${this.solicitudSeleccionadaId}`

          this.errorMsg = ""
          this.$emit("notificacion-enviada", {
            solicitudBecaId: this.solicitudSeleccionadaId,
            carnet,
            ciclo
          })
          return
        }

        this.errorMsg =
          "No fue posible enviar la notificación. Intenta nuevamente."
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
